import { Highlighter } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useHighlights } from '@/hooks/useHighlights';
import type { HighlightTarget } from '@/lib/nip84';

interface HighlightToggleProps {
  event: NostrEvent;
  /** `kind:pubkey:d` coordinate, required when `event.kind` is addressable (30000-39999). */
  addressPointer?: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  size?: 'sm' | 'default';
  className?: string;
}

/**
 * Toggles "highlight mode" for the article body: when on, every public
 * NIP-84 highlight (`kind:9802`) on this specific version is shown
 * inline as a `<mark>`, and logged-in readers can select any passage of
 * text to publish a new highlight of their own (see `HighlightLayer`,
 * which this button controls the visibility of).
 *
 * Unlike `StarButton`/`ZapButton`, this isn't itself the action that
 * publishes anything — it's a visibility switch, styled the same way
 * (count badge + icon) so it reads as part of the same action-button
 * row.
 */
export function HighlightToggle({
  event,
  addressPointer,
  enabled,
  onToggle,
  size = 'sm',
  className,
}: HighlightToggleProps) {
  const target: HighlightTarget = { id: event.id, pubkey: event.pubkey, kind: event.kind, addressPointer };
  const { data, isLoading } = useHighlights(target);
  const count = data?.highlights.length ?? 0;

  return (
    <Button
      type="button"
      variant={enabled ? 'default' : 'outline'}
      size={size}
      onClick={() => onToggle(!enabled)}
      title={enabled ? 'Hide highlights' : 'Show public highlights, and select text to add your own'}
      className={cn('gap-1.5', className)}
    >
      <Highlighter className="size-4" />
      {isLoading ? '' : count}
    </Button>
  );
}
